import React, { useMemo } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, styled } from '@mui/material';

import { Badge, Tooltip, Typography } from '@zendesk/forethought-elements';
import { formatTimestamp } from '../helpers';
import { AiInstructionsTrigger } from './AiInstructionsTrigger';
import { ArticleCardStep } from './ArticleCardStep';
import { AutoflowTool } from './AutoflowTool';
import { buildRenderBlocks } from './buildRenderBlocks';
import { ButtonSelectionStep } from './ButtonSelectionStep';
import { ChatHandoffStatus } from './ChatHandoffStatus';
import { CollapsibleKnowledgeCoachingGroup } from './CollapsibleKnowledgeCoachingGroup';
import { ContentSnippetTrigger } from './ContentSnippetTrigger';
import { CsatSubmissionStep } from './CsatSubmissionStep';
import { CustomEvent } from './CustomEvent';
import { EmbedStep } from './EmbedStep';
import { ExecutionError } from './ExecutionError';
import { ExecutionIndicator } from './ExecutionIndicator';
import { GotoWorkflow } from './GotoWorkflow';
import { ImageStep } from './ImageStep';
import { IndicatorStep } from './IndicatorStep';
import { IntentTrigger } from './IntentTrigger';
import { QuickFeedbackStep } from './QuickFeedbackStep';
import { RelatedArticles } from './RelatedArticles';
import { TextStep } from './TextStep';
import {
  AgentTestTranscriptComponent,
  AiInstructionsTranscriptComponent,
  ArticleCardTranscriptComponent,
  AutoflowToolTranscriptComponent,
  ButtonSelectionTranscriptComponent,
  ContentSnippetTranscriptComponent,
  CsatSubmissionTranscriptComponent,
  CustomEventTranscriptComponent,
  DynamicCardSelectionTranscriptComponent,
  DynamicCardTranscriptComponent,
  EmbedTranscriptComponent,
  ExecutionTranscriptComponent,
  GotoWorkflowTranscriptComponent,
  ImageTranscriptComponent,
  IndicatorTranscriptComponent,
  IntentTriggeredTranscriptComponent,
  MessageBubblePosition,
  QuickFeedbackTranscriptComponent,
  RelatedArticlesTranscriptComponent,
  TextTranscriptComponent,
  TranscriptComponent,
} from './types';
import {
  useGetComponentTopPadding,
  useGetMessageBubblePosition,
} from './utils';
import { ColorMode } from 'src/components/app/types';
import { ApiDynamicCardStep } from 'src/pages/workflow-builder-preview/api-preview/ApiDynamicCardStep';

const getTooltipContent = (transcriptComponent: TranscriptComponent) => {
  return formatTimestamp(transcriptComponent.timestamp + 'Z');
};

const mapTranscriptPropsToComponent = ({
  isAssist,
  messageBubblePosition,
  nextTranscriptComponent,
  t,
  theme = 'light',
  transcriptComponent,
}: {
  isAssist?: boolean;
  messageBubblePosition: MessageBubblePosition;
  nextTranscriptComponent?: TranscriptComponent;
  t: TFunction<'intent-conversation-analytics'>;
  theme?: ColorMode;
  transcriptComponent: TranscriptComponent;
}) => {
  const messageType = transcriptComponent.message_type;
  switch (messageType) {
    case 'text':
      return (
        <TextStep
          messageBubblePosition={messageBubblePosition}
          transcriptComponent={transcriptComponent as TextTranscriptComponent}
        />
      );
    case 'intent_trigger':
      return (
        <IntentTrigger
          theme={theme}
          transcriptComponent={
            transcriptComponent as IntentTriggeredTranscriptComponent
          }
        />
      );
    case 'ai_instructions':
      return (
        <AiInstructionsTrigger
          aiInstructionId={
            (transcriptComponent as AiInstructionsTranscriptComponent)
              .ai_instruction_ids[0]
          }
        />
      );
    case 'content_snippet':
      return (
        <ContentSnippetTrigger
          transcriptComponent={
            transcriptComponent as ContentSnippetTranscriptComponent
          }
        />
      );
    case 'autoflow_tool':
      return (
        <AutoflowTool
          theme={theme}
          transcriptComponent={
            transcriptComponent as AutoflowToolTranscriptComponent
          }
        />
      );
    case 'custom_event':
      return (
        <CustomEvent
          theme={theme}
          transcriptComponent={
            transcriptComponent as CustomEventTranscriptComponent
          }
        />
      );
    case 'execution':
      return (
        <ExecutionIndicator
          message={
            (transcriptComponent as ExecutionTranscriptComponent).message
          }
          theme={theme}
          timestamp={formatTimestamp(transcriptComponent.timestamp + 'Z')}
        />
      );
    case 'indicator':
      return (
        <IndicatorStep
          message={
            (transcriptComponent as IndicatorTranscriptComponent).message
          }
        />
      );
    case 'image':
      return (
        <ImageStep
          transcriptComponent={transcriptComponent as ImageTranscriptComponent}
        />
      );
    case 'embed':
      return (
        <EmbedStep
          transcriptComponent={transcriptComponent as EmbedTranscriptComponent}
        />
      );
    case 'button_selection':
      return (
        <ButtonSelectionStep
          transcriptComponent={
            transcriptComponent as ButtonSelectionTranscriptComponent
          }
        />
      );
    case 'article_card':
      return (
        <ArticleCardStep
          transcriptComponent={
            transcriptComponent as ArticleCardTranscriptComponent
          }
        />
      );
    case 'csat_submission':
      return (
        <CsatSubmissionStep
          transcriptComponent={
            transcriptComponent as CsatSubmissionTranscriptComponent
          }
        />
      );
    case 'goto_workflow':
      return (
        <GotoWorkflow
          theme={theme}
          transcriptComponent={
            transcriptComponent as GotoWorkflowTranscriptComponent
          }
        />
      );
    case 'related_articles':
      return (
        <RelatedArticles
          isAssist={isAssist}
          transcriptComponent={
            transcriptComponent as RelatedArticlesTranscriptComponent
          }
        />
      );
    case 'quick_feedback':
      return (
        <QuickFeedbackStep
          transcriptComponent={
            transcriptComponent as QuickFeedbackTranscriptComponent
          }
        />
      );
    case 'chat_handoff_status_message':
      return (
        <ChatHandoffStatus
          transcriptComponent={
            transcriptComponent as ExecutionTranscriptComponent
          }
        />
      );
    case 'dynamic_card_selection':
      return <></>;
    case 'dynamic_card':
      if (nextTranscriptComponent?.message_type === 'dynamic_card_selection') {
        return (
          <ApiDynamicCardStep
            isTheLatestStep
            isTranscript
            selectedCardValue={
              (
                nextTranscriptComponent as DynamicCardSelectionTranscriptComponent
              ).fields?.value
            }
            step={transcriptComponent as DynamicCardTranscriptComponent}
          />
        );
      }
      return (
        <ApiDynamicCardStep
          isTheLatestStep
          isTranscript
          step={transcriptComponent as DynamicCardTranscriptComponent}
        />
      );
    case 'execution_error':
      return <ExecutionError transcriptComponent={transcriptComponent} />;
    case 'sentiment_analysis':
      return (
        <ExecutionIndicator
          message={
            <>
              {t('revampConversationTranscript.userResponseIdentifiedAs')}
              <Badge
                label={
                  <Typography variant='font11'>
                    {t('chatEventsSection.negative')}
                  </Typography>
                }
                variant='error'
              />
            </>
          }
          timestamp={formatTimestamp(transcriptComponent.timestamp + 'Z')}
        />
      );
    case 'agent_testing':
      return (
        <ExecutionIndicator
          message={
            (transcriptComponent as AgentTestTranscriptComponent).message
          }
          theme={theme}
          timestamp={formatTimestamp(transcriptComponent.timestamp + 'Z')}
        />
      );
    default:
      return (
        <Box sx={{ overflowWrap: 'anywhere' }}>
          {JSON.stringify(transcriptComponent)}
        </Box>
      );
  }
};

export const GenericTranscriptComponent = ({
  hideTimestampTooltip = false,
  isAssist,
  messageBubblePosition,
  nextTranscriptComponent,
  theme = 'light',
  topPadding,
  transcriptComponent,
}: {
  hideTimestampTooltip?: boolean;
  isAssist?: boolean;
  messageBubblePosition: MessageBubblePosition;
  nextTranscriptComponent?: TranscriptComponent;
  theme?: ColorMode;
  topPadding: string;
  transcriptComponent: TranscriptComponent;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const containerAlignment =
    transcriptComponent.message_type === 'quick_feedback'
      ? 'flex-start'
      : transcriptComponent.component_type === 'input'
        ? 'flex-end'
        : 'flex-start';

  const tooltipAlignment =
    transcriptComponent.component_type === 'input'
      ? 'flex-end'
      : transcriptComponent.component_type === 'execution'
        ? 'flex-center'
        : 'flex-start';

  const tooltipWidth =
    transcriptComponent.component_type === 'execution' ||
    transcriptComponent.message_type === 'quick_feedback'
      ? '100%'
      : null;

  const toolTipMaxWidth =
    transcriptComponent.component_type === 'execution' ||
    transcriptComponent.message_type === 'quick_feedback'
      ? '100%'
      : 'calc(100% - 30px)';

  return (
    <Box
      sx={{
        alignItems: 'flex-end',
        display: 'flex',
        gap: '6px',
        justifyContent: containerAlignment,
        paddingTop: topPadding,
      }}
    >
      <Tooltip
        placement='top'
        sx={{
          alignSelf: tooltipAlignment,
          maxWidth: toolTipMaxWidth,
          width: tooltipWidth,
        }}
        tooltipContent={
          hideTimestampTooltip ? null : getTooltipContent(transcriptComponent)
        }
      >
        {mapTranscriptPropsToComponent({
          isAssist,
          messageBubblePosition: messageBubblePosition,
          nextTranscriptComponent: nextTranscriptComponent,
          t,
          theme,
          transcriptComponent: transcriptComponent,
        })}
      </Tooltip>
    </Box>
  );
};

export const RevampConversationTranscript = ({
  proactiveTriggerName,
  transcriptComponents,
}: {
  proactiveTriggerName?: string;
  transcriptComponents: TranscriptComponent[];
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const getMessageBubblePosition =
    useGetMessageBubblePosition(transcriptComponents);
  const getComponentTopPadding =
    useGetComponentTopPadding(transcriptComponents);
  const renderBlocks = useMemo(
    () => buildRenderBlocks(transcriptComponents),
    [transcriptComponents],
  );

  // indicates the start of the chat, shares the same timestamp as the first transcript component
  const chatStartedComp: ExecutionTranscriptComponent | null =
    transcriptComponents.length == 0
      ? null
      : {
          component_type: 'execution',
          message: t('revampConversationTranscript.chatStarted'),
          message_type: 'execution',
          timestamp: transcriptComponents[0].timestamp,
        };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <Typography variant='font11'>
        {t('revampConversationTranscript.title')}
      </Typography>
      <TranscriptContainer>
        {chatStartedComp && (
          <GenericTranscriptComponent
            messageBubblePosition='lone'
            topPadding='0px'
            transcriptComponent={chatStartedComp}
          />
        )}

        {chatStartedComp && proactiveTriggerName && (
          <Box paddingTop='10px'>
            <IndicatorStep
              message={t('revampConversationTranscript.proactiveTrigger', {
                name: proactiveTriggerName,
              })}
            />
          </Box>
        )}

        {renderBlocks.map(block => {
          if (block.type === 'kc-group') {
            const topPadding = getComponentTopPadding(block.startIndex);
            return (
              <Box
                key={`kc-group-${block.startIndex}`}
                sx={{
                  alignItems: 'flex-start',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  paddingTop: topPadding,
                  width: '100%',
                }}
              >
                <CollapsibleKnowledgeCoachingGroup
                  transcriptComponents={block.components}
                />
              </Box>
            );
          }

          const { component: comp, index } = block;
          if (comp.message_type === 'solve_ab_test_variant_assignment') {
            return null;
          }
          const messageBubblePosition = getMessageBubblePosition(index);
          const topPadding = getComponentTopPadding(index);
          const quickFeedbackComponent =
            comp.message_type === 'related_articles' ||
            comp.message_type === 'article_card'
              ? (
                  comp as
                    | RelatedArticlesTranscriptComponent
                    | ArticleCardTranscriptComponent
                ).quick_feedback
              : undefined;

          const key =
            comp.message_type === 'ai_instructions'
              ? `${index}-${
                  (comp as AiInstructionsTranscriptComponent)
                    .ai_instruction_ids[0]
                }`
              : index;

          return (
            <React.Fragment key={key}>
              <GenericTranscriptComponent
                messageBubblePosition={messageBubblePosition}
                nextTranscriptComponent={transcriptComponents[index + 1]}
                topPadding={topPadding}
                transcriptComponent={comp}
              />
              {quickFeedbackComponent && (
                <GenericTranscriptComponent
                  messageBubblePosition={messageBubblePosition}
                  topPadding={topPadding}
                  transcriptComponent={quickFeedbackComponent}
                />
              )}
            </React.Fragment>
          );
        })}
      </TranscriptContainer>
    </Box>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- TODO: fix later
export const TranscriptContainer = styled(Box)`
  border: 1px solid ${({ theme }) => theme.palette.colors.slate[200]};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ul {
    margin-top: 0;
  }
`;
